import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import * as Sentry from '@sentry/react';
import { STATE_MESSAGES } from '@/i18n/ja/states';

interface ErrorFallbackProps {
  error: Error | unknown;
  componentStack?: string | null;
}

/**
 * Sentry ErrorBoundary フォールバックUI
 *
 * Phase 1l 統合リファクタ (#2997) により文字列を i18n/ja/states.ts の
 * STATE_MESSAGES.boundary に移設。hero UX 維持のため layout は既存温存。
 * Sentry.isInitialized() による報告状態表示も維持。
 */
export function ErrorFallback({ error, componentStack }: ErrorFallbackProps) {
  const isDevelopment = import.meta.env.MODE === 'development';
  const isSentryEnabled = Sentry.isInitialized();
  const b = STATE_MESSAGES.boundary;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
      <div className="w-full max-w-2xl">
        <div className="flex justify-center mb-8">
          <div className="p-4 bg-red-100 dark:bg-red-900/20 rounded-full">
            <AlertTriangle
              className="w-16 h-16 text-red-600 dark:text-red-400"
              aria-hidden="true"
            />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-center mb-4 text-slate-900 dark:text-slate-100">
          {b.title}
        </h1>
        <p className="text-center text-slate-600 dark:text-slate-400 mb-8 whitespace-pre-line">
          {b.description}
        </p>

        <div className="flex gap-4 justify-center mb-8">
          <Button
            onClick={() => window.location.reload()}
            size="lg"
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
            {b.reloadLabel}
          </Button>
          <Button
            onClick={() => {
              window.location.href = '/';
            }}
            variant="outline"
            size="lg"
            className="gap-2"
          >
            <Home className="w-4 h-4" aria-hidden="true" />
            {b.homeLabel}
          </Button>
        </div>

        {isDevelopment && (
          <div className="mt-8">
            <Alert variant="destructive">
              <AlertTitle>{b.devDetailsTitle}</AlertTitle>
              <AlertDescription>
                <details className="mt-4">
                  <summary className="cursor-pointer font-medium">
                    {b.devErrorMessageSummary}
                  </summary>
                  <pre className="mt-2 text-xs overflow-auto p-4 bg-slate-900 text-slate-100 rounded">
                    {error instanceof Error ? error.toString() : String(error)}
                  </pre>
                </details>

                {componentStack && (
                  <details className="mt-4">
                    <summary className="cursor-pointer font-medium">
                      {b.devComponentStackSummary}
                    </summary>
                    <pre className="mt-2 text-xs overflow-auto p-4 bg-slate-900 text-slate-100 rounded max-h-64">
                      {componentStack}
                    </pre>
                  </details>
                )}
              </AlertDescription>
            </Alert>
          </div>
        )}

        <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-8">
          {b.helpPrefix}
          <br />
          {isSentryEnabled ? b.footerWithSentry : b.footerWithoutSentry}
        </p>
      </div>
    </div>
  );
}
